import { TZDate, tz } from '@date-fns/tz';
import { format, isValid } from 'date-fns';

// ----------------------------------------------------------------------

/**
 * @Docs
 * https://date-fns.org/docs/format
 * https://github.com/date-fns/tz
 */

export type DatePickerFormat = Date | string | number | null | undefined;

const isValidDate = (date: DatePickerFormat): date is Date | string | number =>
  date !== null && date !== undefined && isValid(new Date(date));

// ----------------------------------------------------------------------

/** Patterns for server timestamps shown to the user. Use them through useFormatDate. */
export const displayFormats = {
  date: 'MMM dd, yyyy', // Sep 10, 2026
  time: 'h:mm a', // 9:52 AM
  longTime: 'hh:mm:ss a', // 09:52:07 AM
  tableDate: 'EEE MMM d, yyyy', // Thu Sep 10, 2026
  dateTime: 'EEE MMM d yyyy, h:mm a', // Thu Sep 10 2026, 9:52 AM
} as const;

/** Patterns for date pickers (the `format` prop). */
export const formatPatterns = {
  dateTime: 'dd MMM yyyy h:mm a', // 17 Apr 2022 12:00 am
  date: 'dd MMM yyyy', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  split: {
    dateTime: 'dd/MM/yyyy h:mm a', // 17/04/2022 12:00 am
    date: 'dd/MM/yyyy', // 17/04/2022
  },
  paramCase: {
    dateTime: 'dd-MM-yyyy h:mm a', // 17-04-2022 12:00 am
    date: 'dd-MM-yyyy', // 17-04-2022
  },
};

// ----------------------------------------------------------------------

/**
 * Formats an absolute instant (a UTC ISO string from the API, a Date, a timestamp) in the given
 * IANA time zone. Returns an empty string for a missing or invalid date. An unknown zone id
 * (date-fns throws a RangeError for it) falls back to the browser zone instead of breaking the page.
 */
export const formatInTimeZone = (date: DatePickerFormat, timeZone: string, template: string): string => {
  if (!isValidDate(date)) return '';
  try {
    return format(date, template, { in: tz(timeZone) });
  } catch {
    return format(date, template);
  }
};

/** The wall-clock fields of `date` (as the picker produced them), read as a time in `timeZone`. */
export const wallClockToInstant = (date: Date, timeZone: string): Date => {
  const instant = new TZDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    timeZone,
  );
  // A plain Date: TZDate.toISOString() would print the zone offset instead of "Z".
  return new Date(instant.getTime());
};

/** The current wall-clock time in `timeZone`, carried by a local Date so that pickers show it as is. */
export const nowAsWallClock = (timeZone: string): Date => {
  const now = TZDate.tz(timeZone);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
};

// ----------------------------------------------------------------------

export const convertFormatStringToUnicodeToken = (str: string) => {
  return str.replaceAll(/[{}]/g, '').replace('[U]', '');
};

export const getLocationIdFromFormat = (formatValue: string) => {
  const date = new Date();
  const unicodeFormat = convertFormatStringToUnicodeToken(formatValue);
  const isUppercase = formatValue.includes('U');
  const locationId = format(date, unicodeFormat);
  return isUppercase ? locationId.toUpperCase() : locationId;
};
